import GearIcon from '@components/basics/icons/gear';
import { RightArrowIcon } from '@components/basics/icons/right-arrow';
import React, { FC, useMemo, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { Link } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import SettingsDialog from './settings-dialog';
import { AnimatePresence } from 'framer-motion';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import { PublishTracPageDraft } from '@webapp/store/tracpage/action-creators';
import { Dispatch } from 'redux';
import theme from '@webapp/theme';
import { TourEvent } from '@common/types/traclink/tour-event';

type StateProps = {
  artistId: string | null;
  changes: number;
  eventDrafts: {
    new: TourEvent[];
    edit: TourEvent[];
  };
};
type DispatchProps = {
  publishTracPage: () => PublishTracPageDraft;
};
type Props = StateProps & DispatchProps;

const mapStateToProps = (state: AppState): StateProps => ({
  artistId: state.activeProfile.id || null,
  changes: state.tracpage.artistPageChanges,
  eventDrafts: {
    new: state.traclink.tourEventDrafts.new,
    edit: Object.values(state.traclink.tourEventDrafts.edit),
  },
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  publishTracPage: () => dispatch(new PublishTracPageDraft()),
});

const ProfileActions: FC<Props> = ({
  changes,
  eventDrafts,
  publishTracPage,
}) => {
  const totalChanges = useMemo(
    () => changes + eventDrafts.edit.length + eventDrafts.new.length,
    [eventDrafts, changes]
  );

  return (
    <ThemeProvider theme={{ ...theme, colors: theme.variants.light }}>
      <DashboardLink to="/dashboard">
        <StyledArrowIcon direction="left" />
        <span className="label">
          <Translate id="NAVBAR.ITEM.DASHBOARD" />
        </span>
      </DashboardLink>
      <PublishButton onClick={publishTracPage} changes={totalChanges} />
      <PageSettings />
    </ThemeProvider>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileActions);

const PageSettings = () => {
  const [openSettings, setOpenSettings] = useState(false);
  return (
    <>
      <SettingsButton onClick={() => setOpenSettings((prev) => !prev)}>
        <StyledGearIcon size={20} />
      </SettingsButton>
      <AnimatePresence initial={false} exitBeforeEnter={true}>
        {openSettings && (
          <SettingsDialog onClose={() => setOpenSettings(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

type PublishButtonProps = {
  changes?: number;
  onClick: () => void;
};

const PublishButton: FC<PublishButtonProps> = ({ changes = 0, onClick }) => {
  const noChanges = changes === 0;
  const formattedChanges = changes > 9 ? '9+' : changes.toString();

  return (
    <PublishCTA disabled={noChanges} onClick={onClick}>
      {!noChanges && <ChangesCounter>{formattedChanges}</ChangesCounter>}
      <Translate id="PROFILE.PUBLISH" />
    </PublishCTA>
  );
};

const StyledGearIcon = styled(GearIcon)`
  path {
    fill: ${({ theme }) => theme.colors.black};
  }
`;

const SettingsButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 9999;
  cursor: pointer;

  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};

  width: 48px;
  height: 48px;
  bottom: 96px;
  right: 26px;
  padding: 12px 10px 12px 10px;
  gap: 8px;
  border-radius: 50%;
`;

const StyledArrowIcon = styled(RightArrowIcon)`
  path {
    fill: ${({ theme }) => theme.colors.black};
  }
`;

const PublishCTA = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 9999;
  cursor: pointer;

  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};

  top: 32px;
  right: 26px;
  padding: 12px 14px 12px 14px;
  border-radius: 90px;
  font-weight: 700;
  font-size: 14px;

  &:disabled {
    background-color: #4d4d4e;
    color: #a6a6a7;
    cursor: not-allowed;
  }
`;

const ChangesCounter = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -8px;
  right: -8px;

  width: 24px;
  height: 24px;

  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  line-height: 0px;
  background-color: #e51f37;
  color: #fff;
`;

const DashboardLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 9999;

  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};

  top: 32px;
  left: 26px;
  padding: 12px 14px 12px 14px;
  gap: 8px;
  border-radius: 90px;
  font-weight: 700;
  font-size: 14px;

  & > .label {
    display: none;
    @media ${({ theme }) => theme.device.tablet} {
      display: inline;
    }
  }
`;
